import { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { WhatsAppIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { whatsappIntegrationConfig } from '@/util/validation/rules/integration-rules';

type MessagingForm = {
  prefilled_message: string;
  include_agent_name: boolean;
  list_button_text: string;
};

interface Props {
  integration: WhatsAppIntegration;
}

const FORM_ID = 'whatsapp-messaging-form';

const Messaging = ({ integration }: Props) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const { updateIntegration, updateStatus } = useIntegrations<WhatsAppIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  const form = useForm<MessagingForm>({
    mode: 'onSubmit',
    defaultValues: {
      prefilled_message: integration?.config?.prefilled_message || '',
      include_agent_name: integration?.config?.include_agent_name ?? false,
      list_button_text: integration?.config?.list_button_text || '',
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  // Sync form with server data when integration config changes
  useEffect(() => {
    reset({
      prefilled_message: integration?.config?.prefilled_message || '',
      include_agent_name: integration?.config?.include_agent_name ?? false,
      list_button_text: integration?.config?.list_button_text || '',
    });
  }, [
    integration?.config?.prefilled_message,
    integration?.config?.include_agent_name,
    integration?.config?.list_button_text,
    reset,
  ]);

  const onSubmit = (values: MessagingForm) => {
    updateIntegration({
      ...integration,
      config: {
        ...integration.config,
        ...values,
        prefilled_message: values.prefilled_message || undefined,
        list_button_text: values.list_button_text.trim() || undefined,
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <div className="w-full">
          <div className="mb-2 flex items-center justify-between">
            <p className="px-1.5 text-sm leading-none font-medium text-foreground-muted">
              {t('integrations.whatsapp.pref_settings.title')}
            </p>
          </div>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateStatus === 'pending'}
            disabled={!isDirty || !canWrite}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-6">
              {/* Prefilled Message */}
              <FormField
                control={control}
                name="prefilled_message"
                rules={whatsappIntegrationConfig.prefilled_message}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('integrations.whatsapp.pref_settings.pre_msg.title')}</FormLabel>
                    <FormControl>
                      <Input
                        id="prefilled_message"
                        placeholder={t('integrations.whatsapp.pref_settings.pre_msg.pholder')}
                        disabled={!canWrite}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('integrations.whatsapp.pref_settings.pre_msg.subtitle')}
                    </FormDescription>
                  </FormItem>
                )}
              />

              {/* List Button Text */}
              <FormField
                control={control}
                name="list_button_text"
                rules={whatsappIntegrationConfig.list_button_text}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('integrations.whatsapp.pref_settings.list_button_text.title')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="list_button_text"
                        placeholder="👈"
                        disabled={!canWrite}
                        maxLength={20}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('integrations.whatsapp.pref_settings.list_button_text.subtitle')}
                    </FormDescription>
                  </FormItem>
                )}
              />

              {/* Show Agent Name */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">
                    {t('integrations.whatsapp.pref_settings.incl_agent.title')}
                  </p>
                  <p className="text-sm text-foreground-muted">
                    {t('integrations.whatsapp.pref_settings.incl_agent.subtitle')}
                  </p>
                </div>
                <Controller
                  name="include_agent_name"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Switch checked={value} onCheckedChange={onChange} disabled={!canWrite} />
                  )}
                />
              </div>
            </div>
          </FieldsetCard>
        </div>
      </form>
    </Form>
  );
};

export default Messaging;
